import { MAX_TWO_DECIMALS_REGEX, numberErrorMessage, requiredErrorMessage } from 'constants/constants';
import { Commodity } from 'models/Farmbox/commodity.model';
import * as Yup from 'yup';

interface TestContextExtended {
    from: {
        value: Commodity;
    }[];
}

const decimalPrecisionTest = (fieldName: string) => Yup.number().defined().test('decimal-precision', `${fieldName} must have at most 2 decimal places`, value => !value || MAX_TWO_DECIMALS_REGEX.test(value.toString()));

export const farmCommodityValidationSchema = Yup.object({
    name: Yup.string().required(requiredErrorMessage).typeError(requiredErrorMessage),
    locationId: Yup.string().required(requiredErrorMessage).typeError(requiredErrorMessage),
    harvestYear: Yup.number().required(requiredErrorMessage).typeError(numberErrorMessage),
    cropId: Yup.number().required(requiredErrorMessage).typeError(numberErrorMessage),
    area: Yup.number().required(requiredErrorMessage).typeError(numberErrorMessage).test(
        'decimal-precision',
        'Field area must have at most 2 decimal places',
        value => {
            if (!value) return true;
            return MAX_TWO_DECIMALS_REGEX.test(value.toString());
        }
    ) .test(
        'max-value',
        'Field area exceeds the allowed limit',
        (value) => {
          if (!value) return true;
          return value <= 1e18; 
        }
      ),
    areaUnit: Yup.string().required(requiredErrorMessage).typeError(requiredErrorMessage),
    wetWeight: Yup.number().required(requiredErrorMessage).typeError(numberErrorMessage)
    .min(1,'Wet weight must be at least 1')
    .test(
        'decimal-precision',
        'Wet weight must have at most 2 decimal places',
        value => {
            if (!value) return true;
            return MAX_TWO_DECIMALS_REGEX.test(value.toString());
        }
    ),
    dryWeight: Yup.number().required(requiredErrorMessage).typeError(numberErrorMessage)
    .min(1,'Dry weight must be at least 1')
    .test(
        'decimal-precision',
        'Dry weight must have at most 2 decimal places',
        value => {
            if (!value) return true;
            return MAX_TWO_DECIMALS_REGEX.test(value.toString());
        }
    ),
    revenuePercentage: Yup.number().required(requiredErrorMessage).typeError(numberErrorMessage)
        .min(1, 'Farm revenue must be at least 1%')
        .max(100, 'Farm revenue cannot exceed 100%')
        .concat(decimalPrecisionTest('Farm revenue')),

    description: Yup.string().required(requiredErrorMessage).typeError(requiredErrorMessage),
    coProducts: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required(requiredErrorMessage).typeError(requiredErrorMessage),
            allocation: Yup.number().required(requiredErrorMessage)
                .typeError(numberErrorMessage)
                .min(1, 'Allocation must be at least 1%')
                .max(99, 'Allocation cannot exceed 99%')
                .test('allocation-sum', 'maximum allocation total is 99%', function () {
                    const values = (this as Yup.TestContext & TestContextExtended).from[1].value.coProducts
                    const totalAllocation = values?.reduce((sum, item) => Number(sum) + Number(item.allocation || 0), 0) ?? 0
                    return totalAllocation <= 99;
                }) 
                .concat(decimalPrecisionTest('Allocation')),
        })
    )
});